// import {NavigationProp} from '@react-navigation/native';
import {AnyAction, Dispatch} from '@reduxjs/toolkit';
import {compile, pathToRegexp} from 'path-to-regexp';
import {RouteTypename} from '@src/Navigation/types/route.type';
import {IUmbracoRouteParams} from '~Umbraco/Interface/Umbraco.interface';
import {navigate as gatsbyNavigate} from 'gatsby';
import {Routes} from '@src/Constant/routes';
import {getActiveRegionPage} from '@src/Store/selectors/useRegionUrl';
import {getContentEndUrl, getEndUrls} from './getContentEndUrl';

const routes: Record<string, Routes> = {
  '/:region/:homepage/': Routes.HOME,

  '/:region/:homepage/watch-home/': Routes.HOME_WATCH,

  '/:region/:homepage/watch-home/section-on-demand/': Routes.VOD_HOME,

  '/:region/:homepage/watch-home/section-on-demand/:category/':
    Routes.VOD_CATEGORY,
  '/contents/watch/:category/': Routes.VOD_SINGLE,

  '/:region/:homepage/watch-home/section-live-tv/': Routes.LIVE_TV,

  '/:region/:homepage/watch-home/section-live-events/': Routes.LIVE_EVENTS,
  // listen
  '/:region/:homepage/listen-home/': Routes.HOME_LISTEN,

  '/:region/:homepage/listen-home/section-radio': Routes.RADIO_HOME,
  '/:region/:homepage/listen-home/radio': Routes.RADIO_HOME,

  '/:region/:homepage/listen-home/section-radio/:category/':
    Routes.RADIO_CATEGORY,
  '/:region/:homepage/listen-home/radio/:category/': Routes.RADIO_CATEGORY,

  '/:region/:homepage/listen-home/section-podcasts/:category/':
    Routes.PODCAST_CATEGORY,

  '/:region/:homepage/listen-home/section-podcasts/': Routes.PODCAST_HOME,
  '/:region/:homepage/listen-home/podcast/': Routes.PODCAST_HOME,

  '/:region/:homepage/pressroom/news/': Routes.NEWS_HOME,

  '/:region/:homepage/pressroom/news/:name/': Routes.NEWS_SINGLE,

  //   Helpdesk
  '/:region/:homepage/helpdesk/': Routes.DynamicPage,

  '/:region/:homepage/helpdesk/language-and-regions/': Routes.DynamicPage,
  '/:region/:homepage/helpdesk/certificates-and-courses/': Routes.DynamicPage,
  '/:region/:homepage/helpdesk/job-board/': Routes.DynamicPage,
  '/:region/:homepage/helpdesk/artists-services/': Routes.DynamicPage,
  '/:region/:homepage/helpdesk/settings-and-preferences/': Routes.DynamicPage,

  //
  '/:region/:homepage/artist-services/:service/': Routes.ARTIST_SERVICE,
  '/:region/:homepage/artist-services/': Routes.ARTIST_SERVICE_HOME,

  // content
  '/contents/': Routes.DynamicPage,
  '/contents/watch/': Routes.DynamicPage,
  '/contents/watch/collection-test/': Routes.DynamicPage,
  '/contents/channels/': Routes.DynamicPage,
  '/contents/sub-content/partners/': Routes.DynamicPage,
};

const findRouteName = (url: string, id: string): Routes | null => {
  let routeName: Routes | null = null;
  let finalUrl = url;
  if (id) {
    const regionPages = getActiveRegionPage();
    const {home, watch, listen, learn} = regionPages;
    if (home && home.id === id) {
      return Routes.HOME;
    }
    if (watch) {
      if (watch.id === id) {
        return Routes.HOME_WATCH;
      } else {
        const {vod, liveEvent, liveTv} = watch.children;
        if (vod && vod.id === id) {
          return Routes.VOD_HOME;
        } else if (liveEvent && liveEvent.id === id) {
          return Routes.LIVE_EVENTS;
        } else if (liveTv && liveTv.id === id) {
          return Routes.LIVE_TV;
        }
      }
    }
    if (listen) {
      if (listen.id === id) {
        return Routes.HOME_LISTEN;
      } else {
        const {podcast, radio} = listen.children;
        if (radio && radio.id === id) {
          return Routes.RADIO_HOME;
        } else if (podcast && podcast.id === id) {
          return Routes.PODCAST_HOME;
        }
      }
    }

    if (learn && learn.id === id) {
      return Routes.HOME_LEARN;
    }
  }

  for (const route of Object.keys(routes)) {
    const regex = pathToRegexp(route, []);
    if (regex.test(finalUrl)) {
      routeName = routes[route];
      break;
    }
  }
  return routeName;
};

export const routesOfType: Record<
  RouteTypename,
  ((params: IUmbracoRouteParams) => any) | any
> = {
  Channel: Routes.WATCH,
  ChannelFolder: Routes.DynamicPage,
  CategoryPage: Routes.VOD_CATEGORY,
  Collection: Routes.VOD_COLLECTION,
  VODItem: Routes.VOD_SINGLE,
  WatchCateg: Routes.VOD_CATEGORY,
  ContentEditorFolder: Routes.DynamicPage,
  HelpdeskPage: Routes.DynamicPage,
  HomePage: Routes.HOME,
  ListenCateg: Routes.HOME_LISTEN,
  LiveEvent: Routes.WATCH,
  LiveEventsPage: Routes.LIVE_EVENTS,
  LiveTVPage: Routes.LIVE_TV,
  News: Routes.NEWS_SINGLE,
  NewsFolder: Routes.NEWS_HOME,
  NewsPage: Routes.NEWS_HOME,
  Partner: Routes.PARTNER_SINGLE,
  Partners: Routes.DynamicPage,
  Playlist: Routes.RADIO_PLAYER,
  Podcast: Routes.PODCAST_SINGLE,
  PodcastItem: Routes.PODCAST_PLAYER, // player
  PodcastCateg: Routes.PODCAST_CATEGORY,
  PodcastMenu: Routes.PODCAST_HOME,
  Radio: Routes.RADIO_PLAYER,
  RadioCateg: Routes.RADIO_CATEGORY,
  SectionPage: (params: {url: any; id: any}) => {
    return params && (params.url || params.id)
      ? findRouteName(params.url, params.id) || 'DynamicPage'
      : 'DynamicPage';
  },
  Unknown: undefined,
  Region: undefined,
  Settings: undefined,
  CMSPage: undefined,
  FormPage: Routes.FORMS,
  ArtistService: Routes.ARTIST_SERVICE,
  ArtistServicesPage: Routes.ARTIST_SERVICE_HOME,
  Paywall: Routes.PAYWALL,
  PaywallFolder: Routes.PAYWALL,
  MemberContentTypesLink: undefined,
  MemberDatas: undefined,
  MembersDatas: undefined,
  MemberDownloaded: undefined,
  MemberMessage: undefined,
  MemberNotify: undefined,
  MemberSaved: undefined,
  MemberWatched: undefined,
  MemberOpportunity: Routes.COURSE_UNIT,
  Opportunity: Routes.OPPORTUNITY_SINGLE,
  ExternalLink: undefined,
  LogoLink: undefined,
  Subscription: undefined,
  SubscriptionsFolder: undefined,
  MemberSearch: undefined,
  HelpdeskFolder: Routes.PROFILE_HELPDESK,
  NewsCateg: Routes.NEWS_HOME,
  TranslationContext: undefined,
  TranslationFolder: undefined,
  TranslationItem: undefined,
  ArtistServiceFolder: Routes.ARTIST_SERVICE_HOME,
  CategBgColor: undefined,
  ContentTag: undefined,
  ContentTags: undefined,
  ContentTagsCateg: undefined,
  LiveEventsMenu: Routes.LIVE_EVENTS,
  PlaylistRadioMenu: Routes.RADIO_HOME,
  Space: undefined,
  VODMenu: Routes.VOD_HOME,
  SubscriptionData: undefined,
  SubscriptionDataFolder: undefined,
  FormFolder: undefined,
  ThankYouPage: '/thank-you/:id',
  ChoiceNugget: undefined,
  Course: Routes.COURSE_SINGLE,
  CourseCategoriesFolder: undefined,
  CourseCategory: Routes.COURSE_CATEGORY,
  CourseExam: undefined,
  CourseTestProgression: undefined,
  CourseUnit: undefined,
  Discovery: undefined,
  DiscoverySlide1: undefined,
  GapfillNugget: undefined,
  JobBoardPage: Routes.JOB_BOARD_PAGE,
  JobHub: Routes.JOB_HUB,
  JobHubsFolder: undefined,
  JobTraining: Routes.SINGLE_JOB,
  JobTrainingFolder: undefined,
  JobVacanciesFolder: undefined,
  JobVacancy: undefined,
  LearnMenu: undefined,
  OpportunityFolder: undefined,
  PartnersPage: Routes.PARTNERS_LANDING,
  PersonalityPages: Routes.PERSONALITY,
  PersonalityQuiz: undefined,
  UnitNuggetAnswer: undefined,
  UnitNuggetGapFillAnswer: undefined,
  UnitProgression: undefined,
  AwardCategoryPage: '/awards/category/:id',
  AwardsArtist: undefined,
  AwardsHomepage: undefined,
  CourseStatus: undefined,
  MiniSitesFolder: undefined,
  AwardVoting: '/awards/voting/:id',
  AwardsCategoryMenu: undefined,
  AwardsCategoryMenuDev: undefined,
  AwardsCategoryMenuFolder: undefined,
  AwardsCategoryMenuFolderDev: undefined,
  CourseShare: undefined,
  EntrepreunarialHub: Routes.ENTREPRENEURIAL_HUB,
  JobProgress: undefined,
  JobShare: undefined,
  LandingPage: undefined,
  LegalsFolder: undefined,
  NavMessage: undefined,
  NavigationMessagesFolder: undefined,
  NetlifyDeploy: undefined,
  ObtainedJob: undefined,
  OpportunityCenter: Routes.OPPORTUNITY_CENTER,
  PersonalityQuiz1: undefined,
  PersonalityType: undefined,
  QuizPage: Routes.HOME_QUIZ,
  SumaryNugget: undefined,
  UmbracoTest: undefined,
  VotingFolder: undefined,
  VotingRecord: undefined,
  PromoCode: undefined,
  PromoCodeFolder: undefined,
  QuizzSurveyCollection: Routes.QUIZZ_SURVEY_COLLECTION,
  QuizzSurvey: Routes.PLAYER_QUIZ_SURVEY,
  QuizzSurveyCategory: Routes.QUIZZ_SURVEY_CATEGORY,
  TextPage: '/text-page/:endUrl',
  HelpdeskCateg: '/faq/:endUrl',
  SurveyQuizzFolder: undefined,
};

export const getTabRoute = (route: string): any | undefined => {
  return;
};

export class RouteFactory<T extends IUmbracoRouteParams> {
  screen: any | null = null;
  routeParams: T;
  constructor(
    private initialParams: T,
    private navigation: any,
    private dispatch?: Dispatch<AnyAction>,
    private headerTitle?: string,
  ) {
    const {url, endUrl, __typename} = this.initialParams;
    const {endUrl1, endUrl2, endUrl: endUrlFromUrl} = getEndUrls(url);
    this.routeParams = {
      ...this.initialParams,
      endUrl:
        endUrl || getContentEndUrl(url || '', __typename) || endUrlFromUrl,
      endUrl1,
      endUrl2,
    };
    this.screen = this.findRouteScreeName();
  }

  private findRouteScreeName(): any {
    const {__typename} = this.routeParams;
    let pageName = routesOfType[__typename];

    const pagePath =
      typeof pageName === 'function' ? pageName(this.routeParams) : pageName;

    if (pagePath) {
      const toPath = compile(pagePath, {encode: encodeURIComponent});
      return toPath({
        ...this.routeParams,
      });
    }
    return;
  }

  getNavigationUrl(): string | undefined {
    const screenFinal = this.screen || this.findRouteScreeName();
    const {url, __typename} = this.routeParams;

    if (__typename === 'LandingPage') {
      return url;
    }

    if (screenFinal) {
      return screenFinal;
    } else if (
      (__typename === 'ExternalLink' || __typename === 'JobVacancy') &&
      url
    ) {
      return url;
    }

    return undefined;
  }

  async navigate(params?: T & any, options?: {replace?: boolean}) {
    const screenFinal = this.screen || this.findRouteScreeName();

    const {url, __typename, liveEventId, id, ...restParams} = this.routeParams;

    const locationState: any = {
      ...params,
      params: {
        ...this.routeParams,
        source:
          screenFinal === Routes.WATCH &&
          (__typename === 'LiveEvent' || __typename === 'Channel')
            ? 'livestream'
            : this.routeParams.source,
      },
    };

    if (__typename === 'FormPage' || __typename === 'AwardVoting') {
      //
      locationState.modal = true;
    }

    if (__typename == 'LandingPage') {
      gatsbyNavigate(url);
      return;
    }

    console.log('🚀 ~ navigate-factory', __typename, url, screenFinal);

    if (
      (__typename === 'ExternalLink' ||
        __typename === 'JobVacancy' ||
        __typename === 'JobHub') &&
      url
    ) {
      window.open(url, '_blank');
      return;
    }

    if (screenFinal) {
      this.navigation.navigate(screenFinal, {
        state: locationState,
        ...options,
      });
    }
  }
}
